import { Loader2 } from 'lucide-react'

import { formatEmploymentFraction } from '@czas/shared'

import { cn } from '@/lib/cn'

import {
  useMyMonthlyProgress,
  type MonthlyProgressStatus,
} from './use-monthly-progress'

const MONTHS_PL = [
  'styczeń',
  'luty',
  'marzec',
  'kwiecień',
  'maj',
  'czerwiec',
  'lipiec',
  'sierpień',
  'wrzesień',
  'październik',
  'listopad',
  'grudzień',
] as const

const STATUS_COLOR: Record<MonthlyProgressStatus, { bar: string; text: string }> = {
  behind: { bar: 'bg-text-muted', text: 'text-text-muted' },
  on_track: { bar: 'bg-brand-accent', text: 'text-brand-accent' },
  completed: { bar: 'bg-success', text: 'text-success' },
  exceeded: { bar: 'bg-warning', text: 'text-warning' },
}

function formatHoursDisplay(hours: number): string {
  const safe = Math.max(0, hours)
  const h = Math.floor(safe)
  const m = Math.round((safe - h) * 60)
  if (m === 0) return `${h}h`
  return `${h}h ${String(m).padStart(2, '0')}`
}

function pluralDni(n: number): string {
  if (n === 1) return 'dnia'
  return 'dni'
}

function monthLabel(yearMonth: string): string {
  const [year, month] = yearMonth.split('-').map(Number)
  const name = MONTHS_PL[(month ?? 1) - 1] ?? ''
  return `${name} ${year}`
}

export function MonthlyProgressCard() {
  const { data: progress, isLoading } = useMyMonthlyProgress()

  if (isLoading) {
    return (
      <div className="rounded-2xl border border-border-subtle bg-bg-elevated px-5 py-6 flex items-center justify-center min-h-[140px]">
        <Loader2 className="h-5 w-5 text-text-muted animate-spin" aria-hidden />
      </div>
    )
  }

  if (!progress) return null

  const color = STATUS_COLOR[progress.status]
  const barWidth = Math.min(100, progress.percentComplete)

  return (
    <div className="rounded-2xl border border-border-subtle bg-bg-elevated px-5 py-5 lg:px-6 lg:py-6 text-left">
      <div className="h-px w-12 bg-gradient-to-r from-brand-accent to-transparent mb-4" />

      <div className="flex items-baseline justify-between gap-4 mb-4">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-text-muted mb-1">
            Mój czas pracy
          </p>
          <p className="text-lg font-extralight text-text-primary tracking-tight capitalize">
            {monthLabel(progress.yearMonth)}
          </p>
        </div>
        <div className="text-right">
          <p
            className={cn(
              'text-3xl font-extralight tabular-nums leading-none',
              color.text,
            )}
          >
            {progress.percentComplete}
            <span className="text-base ml-0.5">%</span>
          </p>
          <p className="text-[10px] text-text-faint uppercase tracking-wider mt-1">
            etatu
          </p>
        </div>
      </div>

      <div className="mb-5">
        <div className="h-1.5 rounded-full bg-bg-surface overflow-hidden mb-2">
          <div
            className={cn(
              'h-full transition-all duration-500 ease-out',
              color.bar,
            )}
            style={{ width: `${barWidth}%` }}
          />
        </div>
        <div className="flex items-baseline justify-between text-sm">
          <span className="text-text-primary font-mono tabular-nums">
            {formatHoursDisplay(progress.totalCredited)}
          </span>
          <span className="text-text-muted font-mono tabular-nums">
            / {formatHoursDisplay(progress.target)}
          </span>
        </div>
        {progress.vacationDays > 0 && (
          <p className="text-[11px] text-text-faint mt-1">
            w tym {formatHoursDisplay(progress.vacationHours)} z{' '}
            {progress.vacationDays} {pluralDni(progress.vacationDays)}{' '}
            nieobecności ({formatHoursDisplay(progress.vacationDayHours)} za
            pełny dzień
            {progress.vacationByType.CHOROBOWY.days > 0 && (
              <>
                , L4 po{' '}
                {formatHoursDisplay(progress.vacationByType.CHOROBOWY.hours /
                  progress.vacationByType.CHOROBOWY.days)}
              </>
            )}
            )
          </p>
        )}
        {progress.unpaidLeaveDays > 0 && (
          <p className="text-[11px] text-text-faint mt-1">
            {progress.unpaidLeaveDays} {pluralDni(progress.unpaidLeaveDays)} urlopu
            bezpłatnego — nie wlicza się do godzin
          </p>
        )}
        <p className="text-[11px] text-text-faint mt-2">
          Norma {monthLabel(progress.yearMonth)}: {progress.fullTimeNorm}h ·
          Twój etat:{' '}
          {formatEmploymentFraction(progress.employmentFraction)}
          {progress.employmentFractionAssumed && (
            <span className="text-warning"> (nieustawiony — przyjęto pełny)</span>
          )}
        </p>
      </div>

      <div className="grid grid-cols-3 gap-3 pt-4 border-t border-border-subtle/60">
        <Stat
          label="Pozostało"
          value={formatHoursDisplay(progress.remainingHours)}
          muted={
            progress.status === 'completed' || progress.status === 'exceeded'
          }
        />
        <Stat
          label="Dni robocze"
          value={String(progress.workingDaysRemaining)}
          muted={progress.workingDaysRemaining === 0}
        />
        <Stat
          label="Średnio/dzień"
          value={
            progress.workingDaysRemaining > 0
              ? formatHoursDisplay(progress.averageHoursPerRemainingDay)
              : '—'
          }
          muted={progress.workingDaysRemaining === 0}
        />
      </div>
    </div>
  )
}

function Stat({
  label,
  value,
  muted,
}: {
  label: string
  value: string
  muted?: boolean
}) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-text-faint mb-1">
        {label}
      </p>
      <p
        className={cn(
          'text-base font-mono tabular-nums',
          muted ? 'text-text-muted' : 'text-text-primary',
        )}
      >
        {value}
      </p>
    </div>
  )
}
